// src/communaute/Messagerie.jsx
//
// Messagerie en deux colonnes : conversations à gauche, discussion à droite.
// Temps réel : rafraîchissement toutes les 5 s dans la discussion ouverte,
// toutes les 30 s pour la liste (l'app mobile utilise Firestore en plus).

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  BadgeCheck,
  BellOff,
  CalendarDays,
  FileText,
  Info,
  MessageSquare,
  PenSquare,
  Send,
  Trash2,
  User,
  Users,
} from 'lucide-react';

import {
  envoyerMessage,
  getConversationDetails,
  getConversations,
  getMessages,
  getNouveauxMessages,
  marquerConversationLue,
  monId,
  supprimerMessage,
} from './api';
import { DetailsConversationModal, NouvelleConversationModal } from './MessagerieModals';
import { heure, heureCourte } from './normalize';
import { Avatar, Chargement, Spinner, Vide, btnPrimaire, champ, confirmer, toast } from './ui';

const FILTRES = [
  ['tous', 'Tous'],
  ['prive', 'Privé'],
  ['groupe', 'Groupes'],
  ['archives', 'Archives'],
];

// ══════════════════════════════════════════════════════
//  PAGE MESSAGERIE
// ══════════════════════════════════════════════════════
export default function Messagerie({ convInitialeId = null, onNonLusChange, onOuvrirEvenement, onOuvrirPost }) {
  const [filtre, setFiltre] = useState('tous');
  const [conversations, setConversations] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [active, setActive] = useState(null);
  const [nouveau, setNouveau] = useState(false);
  const [details, setDetails] = useState(false);
  const enCours = useRef(false);
  const notifierNonLus = useRef(onNonLusChange);
  notifierNonLus.current = onNonLusChange;

  const charger = useCallback(
    async (silencieux = false) => {
      if (enCours.current) return;
      enCours.current = true;
      if (!silencieux) setChargement(true);
      const l = await getConversations(filtre);
      enCours.current = false;
      setConversations(l);
      setChargement(false);
      // garde la conversation active à jour (titre, muet, archive…)
      setActive((a) => (a ? { ...a, ...(l.find((c) => c.id === a.id) || {}) } : a));
      notifierNonLus.current?.();
    },
    [filtre],
  );

  useEffect(() => {
    charger();
  }, [charger]);

  useEffect(() => {
    const t = setInterval(() => charger(true), 30000);
    return () => clearInterval(t);
  }, [charger]);

  // Ouverture directe (depuis une notification)
  useEffect(() => {
    if (!convInitialeId) return;
    getConversationDetails(convInitialeId).then((c) => c && setActive(c));
  }, [convInitialeId]);

  const ouvrir = (c) => {
    setActive(c);
    setConversations((l) => l.map((x) => (x.id === c.id ? { ...x, nonLus: 0 } : x)));
  };

  return (
    <div className="grid h-[calc(100vh-200px)] min-h-[560px] overflow-hidden rounded-2xl border border-gray-200 bg-white lg:grid-cols-[360px_1fr]">
      {/* ══ LISTE ══ */}
      <aside className={`flex min-h-0 flex-col border-gray-200 lg:border-r ${active ? 'hidden lg:flex' : 'flex'}`}>
        <div className="flex items-center justify-between px-4 pb-2 pt-4">
          <h3 className="text-lg font-bold text-gray-900">Discussions</h3>
          <button onClick={() => setNouveau(true)} className={`${btnPrimaire} !px-4 !py-2`}>
            <PenSquare size={16} /> Nouveau
          </button>
        </div>
        <div className="flex gap-1 overflow-x-auto px-3 pb-2">
          {FILTRES.map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFiltre(val)}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold ${filtre === val ? 'bg-[#7C3AED] text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {chargement ? (
            <Chargement />
          ) : conversations.length === 0 ? (
            <Vide
              icone={MessageSquare}
              titre={
                { tous: 'Aucune conversation', prive: 'Aucune discussion privée', groupe: 'Aucun groupe', archives: 'Aucune archive' }[
                filtre
                ]
              }
            />
          ) : (
            conversations.map((c) => (
              <LigneConversation key={c.id} c={c} active={active?.id === c.id} onClick={() => ouvrir(c)} />
            ))
          )}
        </div>
      </aside>

      {/* ══ DISCUSSION ══ */}
      <section className={`min-h-0 flex-col ${active ? 'flex' : 'hidden lg:flex'}`}>
        {active ? (
          <Chat
            key={active.id}
            conv={active}
            onRetour={() => setActive(null)}
            onInfos={() => setDetails(true)}
            onActivite={() => charger(true)}
            onOuvrirEvenement={onOuvrirEvenement}
            onOuvrirPost={onOuvrirPost}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <Vide icone={MessageSquare} titre="Sélectionnez une conversation" sous="ou démarrez-en une nouvelle" />
          </div>
        )}
      </section>

      <NouvelleConversationModal
        ouvert={nouveau}
        onFermer={() => setNouveau(false)}
        onOuverte={(c) => {
          setNouveau(false);
          setActive(c);
          charger(true);
        }}
      />

      {active && (
        <DetailsConversationModal
          conversation={active}
          ouvert={details}
          onFermer={() => setDetails(false)}
          onChange={() => charger(true)}
          onQuittee={(id) => {
            setDetails(false);
            setActive(null);
            setConversations((l) => l.filter((c) => c.id !== id));
            charger(true);
          }}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  LIGNE DE LA LISTE
// ══════════════════════════════════════════════════════
function LigneConversation({ c, active, onClick }) {
  const nonLus = c.nonLus > 0;
  const dernier = c.dernierMessage;
  const apercu = !dernier
    ? 'Démarrez la conversation'
    : `${dernier.isMine ? 'Vous : ' : ''}${dernier.type === 'evenement' ? '📅 Événement partagé' : dernier.type === 'post' ? '📝 Publication partagée' : dernier.contenu
    }`;

  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-4 py-3 text-left transition ${active ? 'bg-[#7C3AED]/15' : 'hover:bg-gray-100'
        }`}
    >
      <div className="relative">
        <Avatar src={c.photo} taille={50} icone={c.estGroupe ? Users : User} />
        {c.estGroupe && (
          <span className="absolute -bottom-0.5 -right-0.5 rounded-full border-2 border-white bg-[#7C3AED] p-0.5">
            <Users size={10} className="text-white" />
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`truncate text-[15px] text-gray-900 ${nonLus ? 'font-bold' : 'font-semibold'}`}>{c.titre}</span>
          {c.muet && <BellOff size={13} className="shrink-0 text-gray-300" />}
          <span className={`ml-auto shrink-0 text-[11px] ${nonLus ? 'font-bold text-[#9B59B6]' : 'text-gray-400'}`}>
            {heureCourte(c.date)}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-2">
          <span className={`truncate text-[13px] ${nonLus ? 'font-medium text-gray-700' : 'text-gray-400'}`}>{apercu}</span>
          {nonLus && (
            <span className="ml-auto shrink-0 rounded-full bg-[#7C3AED] px-2 text-[11px] font-bold leading-5 text-white">
              {c.nonLus > 99 ? '99+' : c.nonLus}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ══════════════════════════════════════════════════════
//  DISCUSSION
// ══════════════════════════════════════════════════════
function Chat({ conv, onRetour, onInfos, onActivite, onOuvrirEvenement, onOuvrirPost }) {
  const [messages, setMessages] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [chargementAnciens, setChargementAnciens] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [texte, setTexte] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const zone = useRef(null);
  const allerEnBas = useRef(true);
  const hauteurAvant = useRef(null);
  const recuperation = useRef(false);
  const moi = monId();

  const ajouterSansDoublon = (liste, nouveaux) => {
    const ids = new Set(liste.map((m) => m.id));
    return [...liste, ...nouveaux.filter((m) => !ids.has(m.id))];
  };

  // ── Chargement initial ──
  useEffect(() => {
    let annule = false;
    (async () => {
      const r = await getMessages(conv.id, 1);
      if (annule) return;
      setMessages(r.items);
      setPage(r.page);
      setLastPage(r.lastPage);
      setChargement(false);
      allerEnBas.current = true;
      await marquerConversationLue(conv.id);
      onActivite?.();
    })();
    return () => {
      annule = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.id]);

  // ── Nouveaux messages (toutes les 5 s) ──
  useEffect(() => {
    const t = setInterval(async () => {
      if (recuperation.current || document.hidden) return;
      recuperation.current = true;
      const dernierId = messages.length ? messages[messages.length - 1].id : 0;
      const nouveaux = await getNouveauxMessages(conv.id, dernierId);
      recuperation.current = false;
      if (nouveaux.length) {
        const el = zone.current;
        allerEnBas.current = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 120;
        setMessages((l) => ajouterSansDoublon(l, nouveaux));
        marquerConversationLue(conv.id);
      }
    }, 5000);
    return () => clearInterval(t);
  }, [conv.id, messages]);

  // ── Position du scroll après rendu ──
  useLayoutEffect(() => {
    const el = zone.current;
    if (!el) return;
    if (hauteurAvant.current !== null) {
      // anciens messages ajoutés en haut : on garde la position
      el.scrollTop = el.scrollHeight - hauteurAvant.current;
      hauteurAvant.current = null;
    } else if (allerEnBas.current) {
      el.scrollTop = el.scrollHeight;
      allerEnBas.current = false;
    }
  }, [messages]);

  const chargerAnciens = async () => {
    if (chargementAnciens || page >= lastPage) return;
    setChargementAnciens(true);
    const r = await getMessages(conv.id, page + 1);
    hauteurAvant.current = zone.current?.scrollHeight ?? null;
    setMessages((l) => {
      const ids = new Set(l.map((m) => m.id));
      return [...r.items.filter((m) => !ids.has(m.id)), ...l];
    });
    setPage(r.page);
    setLastPage(r.lastPage);
    setChargementAnciens(false);
  };

  const envoyer = async (e) => {
    e?.preventDefault();
    const t = texte.trim();
    if (!t || envoi) return;
    setEnvoi(true);
    const m = await envoyerMessage(conv.id, t);
    setEnvoi(false);
    if (!m) return toast('Message non envoyé');
    allerEnBas.current = true;
    setMessages((l) => ajouterSansDoublon(l, [{ ...m, isMine: true }]));
    setTexte('');
    onActivite?.();
  };

  const supprimer = async (m) => {
    if (!(await confirmer({ titre: 'Supprimer ce message ?', bouton: 'Supprimer' }))) return;
    if (await supprimerMessage(m.id)) setMessages((l) => l.filter((x) => x.id !== m.id));
    else toast('Échec de la suppression');
  };

  const estAMoi = (m) => m.isMine || (moi > 0 && m.sender.id === moi);

  return (
    <>
      {/* En-tête */}
      <header className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
        <button onClick={onRetour} className="rounded-full p-1.5 text-gray-600 hover:bg-gray-100 lg:hidden">
          <ArrowLeft size={20} />
        </button>
        <Avatar src={conv.photo} taille={40} icone={conv.estGroupe ? Users : User} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-bold text-gray-900">{conv.titre}</p>
          {conv.estGroupe && <p className="text-xs text-gray-500">{conv.nbrParticipants} participants</p>}
        </div>
        <button onClick={onInfos} className="rounded-full p-2 text-gray-600 hover:bg-gray-100" title="Infos">
          <Info size={20} />
        </button>
      </header>

      {/* Messages */}
      <div
        ref={zone}
        onScroll={(e) => e.currentTarget.scrollTop < 60 && chargerAnciens()}
        className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-4 py-4"
      >
        {chargementAnciens && (
          <div className="flex justify-center py-2">
            <Spinner taille={20} />
          </div>
        )}
        {chargement ? (
          <Chargement />
        ) : messages.length === 0 ? (
          <Vide icone={MessageSquare} titre="Aucun message" sous="Envoyez le premier !" />
        ) : (
          messages.map((m) =>
            m.estSysteme ? (
              <div key={m.id} className="flex justify-center py-1">
                <span className="rounded-full bg-gray-100 px-3.5 py-1.5 text-center text-xs italic text-gray-500">
                  {m.contenu}
                </span>
              </div>
            ) : (
              <Bulle
                key={m.id}
                m={m}
                moi={estAMoi(m)}
                groupe={conv.estGroupe}
                onSupprimer={() => supprimer(m)}
                onOuvrirEvenement={onOuvrirEvenement}
                onOuvrirPost={onOuvrirPost}
              />
            ),
          )
        )}
      </div>

      {/* Saisie */}
      <form onSubmit={envoyer} className="flex items-end gap-2 border-t border-gray-200 px-4 py-3">
        <textarea
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) envoyer(e);
          }}
          rows={1}
          placeholder="Votre message…"
          className={`${champ} max-h-36 resize-none rounded-3xl`}
        />
        <button
          type="submit"
          disabled={!texte.trim() || envoi}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] text-white disabled:opacity-40"
        >
          {envoi ? <Spinner taille={18} className="!text-white" /> : <Send size={18} />}
        </button>
      </form>
    </>
  );
}

// ══════════════════════════════════════════════════════
//  BULLE
// ══════════════════════════════════════════════════════
function Bulle({ m, moi, groupe, onSupprimer, onOuvrirEvenement, onOuvrirPost }) {
  const carte = (image, Icone, label, titre, sous, onClick) => (
    <button
      onClick={onClick}
      className="block w-60 overflow-hidden rounded-xl bg-black/25 text-left transition hover:bg-black/35"
    >
      {image && <img src={image} alt="" className="h-28 w-full object-cover" />}
      <div className="p-2.5">
        <p className="flex items-center gap-1.5 text-[10.5px] font-bold text-[#c9a3e0]">
          <Icone size={13} /> {label}
        </p>
        <p className="mt-1 line-clamp-2 text-[13.5px] font-bold text-white">{titre}</p>
        {sous && <p className="mt-0.5 text-xs text-gray-500">{sous}</p>}
      </div>
    </button>
  );

  return (
    <div className={`group flex items-end gap-2 ${moi ? 'justify-end' : 'justify-start'}`}>
      {!moi && groupe && <Avatar src={m.sender.photo} taille={28} />}

      {moi && (
        <button
          onClick={onSupprimer}
          className="mb-2 rounded-full p-1.5 text-gray-300 opacity-0 transition hover:bg-gray-100 hover:text-red-600 group-hover:opacity-100"
          title="Supprimer"
        >
          <Trash2 size={15} />
        </button>
      )}

      <div
        className={`max-w-[72%] ${m.type === 'texte' ? 'px-3.5 py-2.5' : 'p-2'} ${moi
            ? 'rounded-2xl rounded-br-md bg-gradient-to-r from-[#7C3AED] to-[#5B21B6]'
            : 'rounded-2xl rounded-bl-md bg-gray-100'
          }`}
      >
        {!moi && groupe && (
          <p className="mb-1 flex items-center gap-1 text-[11.5px] font-bold text-[#9B59B6]">
            {m.sender.nom}
            {m.sender.isAbonne && <BadgeCheck size={12} />}
          </p>
        )}

        {m.type === 'evenement' && m.evenement
          ? carte(m.evenement.image, CalendarDays, 'Événement', m.evenement.nom, m.evenement.ville, () =>
            onOuvrirEvenement?.(m.evenement.id),
          )
          : m.type === 'post' && m.post
            ? carte(m.post.image, FileText, 'Publication', m.post.titre, null, () => onOuvrirPost?.(m.post.id))
            : (
              <p
                className={`whitespace-pre-line break-words text-[14.5px] leading-snug ${moi ? 'text-white' : 'text-gray-900'}`}
              >
                {m.contenu}
              </p>
            )}

        <p className={`mt-1 text-right text-[10px] ${moi ? 'text-white/70' : 'text-gray-400'}`}>{heure(m.date)}</p>
      </div>
    </div>
  );
}